import type { RefCallback } from 'react'
import { useInView } from 'react-intersection-observer'

import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAppSelector } from 'hooks/reduxHooks'

type StructuredFacilitiesOffer = App.HotelOffer | App.Offer

function getStructuredFacilities(offerDetails: StructuredFacilitiesOffer): Array<App.StructuredFacilityGroup> | undefined {
  return 'structuredFacilities' in offerDetails ? offerDetails.structuredFacilities : undefined
}

function selectIsInFacilitiesReworkExperiment(state: App.State): boolean {
  const variationKey = state.optimizely.optimizelyExperiments[Experiments.croFacilitiesRework.id]?.variationKey

  return !!variationKey && variationKey.toLowerCase() !== 'off'
}

interface StructuredFacilitiesReworkState {
  structuredFacilitiesReworkActivationRef: RefCallback<HTMLDivElement>
  structuredFacilitiesRework?: Array<App.StructuredFacilityGroup>
  shouldTrackFacilitiesExperimentInView: boolean
}

export interface StructuredFacilitiesActivationPlacement {
  insertIndex: number
}

export function buildStructuredFacilitiesActivationPlacement(options: {
  shouldTrackFacilitiesExperimentInView: boolean
  mountedFacilitiesExperimentActivation: boolean
  facilitiesSectionInsertIndex: number
}): StructuredFacilitiesActivationPlacement | undefined {
  const {
    shouldTrackFacilitiesExperimentInView,
    mountedFacilitiesExperimentActivation,
    facilitiesSectionInsertIndex,
  } = options

  if (!shouldTrackFacilitiesExperimentInView || mountedFacilitiesExperimentActivation) {
    return undefined
  }

  return {
    insertIndex: facilitiesSectionInsertIndex,
  }
}

function useStructuredFacilitiesReworkForFacilities(
  structuredFacilities: Array<App.StructuredFacilityGroup> | undefined,
): StructuredFacilitiesReworkState {
  const hasStructuredFacilities = !!structuredFacilities?.length
  const isInExperimentCohort = useAppSelector(selectIsInFacilitiesReworkExperiment)
  const [structuredFacilitiesReworkActivationRef, structuredFacilitiesReworkInView] = useInView({
    triggerOnce: true,
    threshold: 0.25,
  })
  const { isAnyVariant } = useExperiment(Experiments.croFacilitiesRework, {
    // Only expose users who can see the structured facilities experience. Control
    // users on legacy-only offers are excluded to keep cohort denominators aligned.
    canActivate:
      structuredFacilitiesReworkInView &&
      isInExperimentCohort &&
      hasStructuredFacilities,
  })
  const shouldTrackFacilitiesExperimentInView = hasStructuredFacilities

  return {
    structuredFacilitiesReworkActivationRef,
    structuredFacilitiesRework: isAnyVariant && hasStructuredFacilities ? structuredFacilities : undefined,
    shouldTrackFacilitiesExperimentInView,
  }
}

export function usePropertyStructuredFacilitiesRework(
  property: App.Property.Property,
): StructuredFacilitiesReworkState {
  return useStructuredFacilitiesReworkForFacilities(property.structuredFacilities)
}

function useStructuredFacilitiesRework(offerDetails: StructuredFacilitiesOffer): StructuredFacilitiesReworkState {
  return useStructuredFacilitiesReworkForFacilities(getStructuredFacilities(offerDetails))
}

export default useStructuredFacilitiesRework
